"use client";
import { jsx } from "react/jsx-runtime";
import { isBD } from "../businessType.lib.js";
import { createContext, memo, useRef, useState, useEffect, useContext } from "react";
import "../../components/Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../components/Chrome/BrowseContext/hook.js";
import { useCheckScreen } from "../../hooks/useCheckScreen/index.js";
import { LOCALE_MAP } from "./constants.js";
import { getQueryString } from "./lib.js";
import { queryCriteo, findCriteoPlacement } from "./utils.js";
const COMPONENT_NAME = "CriteoProviderClientComponent";
const CriteoProviderContext = createContext(void 0);
const CriteoProviderComponent = ({
  bdWarehouseNumber,
  criteoAPIConfigurationSettings,
  concurrency = 1,
  productTileConfigData,
  locale,
  children,
  searchParams,
  searchResult,
  listSize = 24,
  pdpStatus,
  isTargetingDisabled,
  isOneTrustTargetingEnabled,
  isSearchResultsLoading,
  categoryId,
  isCategoryPage,
  itemNumber,
  price,
  criteoUserCookie
}) => {
  var _a, _b, _c;
  const ref = useRef(null);
  const { isMobile } = useCheckScreen();
  const isTargetingEnabled = isOneTrustTargetingEnabled && !isTargetingDisabled;
  const isCanada = locale === "en-ca" || locale === "fr-ca";
  const [isLoadingCriteoData, setIsLoadingCriteoData] = useState(true);
  const [sponsoredProductData, setSponsoredProductData] = useState();
  const [recommendedProductData, setRecommendedProductData] = useState();
  const [commerceData, setCommerceData] = useState();
  const [butterflyData, setButterflyData] = useState();
  const [searchPageData, setSearchPageData] = useState();
  const [videoSpotlightData, setVideoSpotlightData] = useState();
  const [sponsoredProductError, setSponsoredProductError] = useState();
  const [recommendedProductError, setRecommendedProductError] = useState();
  const [commerceError, setCommerceError] = useState();
  const [butterflyError, setButterflyError] = useState();
  const [searchPageError, setSearchPageError] = useState();
  const [videoSpotlightError, setVideoSpotlightError] = useState();
  const {
    endpoint,
    required_request_parameters = {},
    timeout,
    connection_timeout
  } = criteoAPIConfigurationSettings;
  const requiredParameters = isBD() && locale === "en-us" ? required_request_parameters[(_a = LOCALE_MAP) == null ? void 0 : _a["USBD"]] : required_request_parameters[(_b = LOCALE_MAP) == null ? void 0 : _b[locale]];
  const partnerId = requiredParameters == null ? void 0 : requiredParameters["criteo-partner-id"];
  const nolog = requiredParameters == null ? void 0 : requiredParameters["nolog"];
  const regionId = isBD() ? bdWarehouseNumber : requiredParameters == null ? void 0 : requiredParameters["regionId"];
  const category = (requiredParameters == null ? void 0 : requiredParameters["specialty-carousel-category"]) || "specialtyhomepage";
  const isSearchPage = !!searchParams || categoryId;
  const isSearchPageReady = (categoryId || !!searchResult) && !isSearchResultsLoading;
  const searchResultDocs = searchResult == null ? void 0 : searchResult.docs;
  const searchResultsItems = searchResultDocs ? (_c = searchResult == null ? void 0 : searchResult.docs) == null ? void 0 : _c.reduce(
    (itemNumbers, { group_id, item_fsa_eligible, item_chdi_eligible }) => {
      if (group_id && !item_fsa_eligible && !item_chdi_eligible) {
        if (itemNumbers) {
          return `${itemNumbers}|${group_id}`;
        } else {
          return group_id;
        }
      }
      return itemNumbers;
    },
    ""
  ) : "";
  const bdWHNumber = bdWarehouseNumber;
  useEffect(() => {
    if (isSearchResultsLoading) {
      setIsLoadingCriteoData(true);
    }
  }, [isSearchResultsLoading]);
  useEffect(() => {
    const useEffectObjectString = JSON.stringify({
      categoryId,
      isCategoryPage,
      isSearchPage,
      isSearchPageReady,
      searchResultsItems,
      criteoUserCookie,
      isTargetingEnabled,
      keyword: searchParams == null ? void 0 : searchParams.keyword,
      refine: searchParams == null ? void 0 : searchParams.refine,
      currentPage: searchParams == null ? void 0 : searchParams.currentPage,
      isSearchResultsLoading,
      category,
      commerceData,
      commerceError,
      connection_timeout,
      endpoint,
      isCanada,
      isMobile,
      itemNumber,
      listSize,
      nolog,
      partnerId,
      price,
      regionId,
      searchParams,
      searchResultDocs,
      sponsoredProductError,
      timeout,
      pdpStatus
    });
    if ((ref == null ? void 0 : ref.current) !== useEffectObjectString) {
      if (pdpStatus === "NA") {
        return;
      }
      if (criteoUserCookie && (!isSearchPage || isSearchPageReady) && (isTargetingEnabled || isCanada)) {
        const sharedQueryParams = {
          block: isTargetingEnabled ? 0 : 1,
          "criteo-partner-id": partnerId,
          nolog,
          regionId,
          "retailer-visitor-id": criteoUserCookie
        };
        if (!searchParams && !categoryId) {
          if (pdpStatus === "outOfStock" || pdpStatus === "inStock") {
            setIsLoadingCriteoData(true);
            const getCriteoPdpRes = async () => {
              var _a2, _b2, _c2, _d, _e, _f;
              const sharedQueryConfigurationParams = {
                connection_timeout,
                endpoint,
                isMobile,
                sharedQueryParams,
                timeout
              };
              const isDevEnvironment = process.env.NODE_ENV === "development";
              const nologParam = `${nolog ? `nolog=${nolog}&` : ""}`;
              const queryString = `${nologParam}criteo-partner-id=${partnerId}&environment=d&retailer-visitor-id=${criteoUserCookie}&page-id=viewItem_API_${isMobile ? "mobile" : "desktop"}&event-type=viewItem&item=${itemNumber}&price=${price}&availability=${pdpStatus === "outOfStock" ? 0 : 1}${isDevEnvironment ? "&regionId=test" : ""}`;
              const {
                data: criteoPdpData,
                error: criteoPdpError
              } = await queryCriteo({
                ...sharedQueryConfigurationParams,
                placementName: "SponsoredProductsCarousel",
                queryString,
                returnAllPlacements: true
              });
              if (criteoPdpData) {
                const sponsoredProductKey = `viewItem_API_${isMobile ? "mobile" : "desktop"}-Carousel`.toLowerCase();
                const sponsoredVideoProductKey = `viewItem_API_${isMobile ? "mobile" : "desktop"}-Video`.toLowerCase();
                const _sponsoredProductData = (_c2 = (_b2 = (_a2 = criteoPdpData.placements) == null ? void 0 : _a2.find(
                  (placement) => {
                    return placement[sponsoredProductKey];
                  }
                )) == null ? void 0 : _b2[sponsoredProductKey]) == null ? void 0 : _c2[0];
                const _sponsoredProductVideoData = (_f = (_e = (_d = criteoPdpData.placements) == null ? void 0 : _d.find(
                  (placement) => {
                    return placement[sponsoredVideoProductKey];
                  }
                )) == null ? void 0 : _e[sponsoredVideoProductKey]) == null ? void 0 : _f[0];
                if (_sponsoredProductData) {
                  setSponsoredProductData({
                    OnLoadBeacon: criteoPdpData.OnLoadBeacon,
                    "page-uid": criteoPdpData.OnLoadBeacon,
                    placement: _sponsoredProductData
                  });
                }
                if (_sponsoredProductVideoData) {
                  setVideoSpotlightData({
                    OnLoadBeacon: _sponsoredProductVideoData == null ? void 0 : _sponsoredProductVideoData.OnLoadBeacon,
                    "page-uid": _sponsoredProductVideoData == null ? void 0 : _sponsoredProductVideoData.OnLoadBeacon,
                    placement: _sponsoredProductVideoData
                  });
                }
              } else if (criteoPdpError) {
                setSponsoredProductError(
                  criteoPdpError
                );
                setVideoSpotlightError(
                  criteoPdpError
                );
              }
              setIsLoadingCriteoData(false);
            };
            getCriteoPdpRes();
          } else {
            const getCriteoHomePageResp = async () => {
              setIsLoadingCriteoData(true);
              const sharedQueryConfigurationParams = {
                connection_timeout,
                endpoint,
                isMobile,
                sharedQueryParams,
                timeout
              };
              const {
                data: carouselPlacements,
                error: carouselPlacementsError
              } = await queryCriteo({
                ...sharedQueryConfigurationParams,
                placementName: "Carousel",
                returnAllPlacements: true
              });
              const {
                data: recommendedProductData2,
                error: recommendedProductError2
              } = await queryCriteo({
                ...sharedQueryConfigurationParams,
                category,
                placementName: "SpecialtyCarousel"
              });
              if (carouselPlacements) {
                const sponsoredProductKey = `viewHome_API_${isMobile ? "mobile" : "desktop"}-Carousel`;
                const sponsoredProductData2 = findCriteoPlacement(
                  sponsoredProductKey,
                  carouselPlacements.placements
                );
                if (sponsoredProductData2) {
                  setSponsoredProductData({
                    OnLoadBeacon: carouselPlacements.OnLoadBeacon,
                    "page-uid": carouselPlacements.OnLoadBeacon,
                    placement: sponsoredProductData2
                  });
                }
                const commerceKey = `viewHome_API_${isMobile ? "mobile" : "desktop"}-CommerceDisplay`;
                const commerceDataPlacement = findCriteoPlacement(
                  commerceKey,
                  carouselPlacements.placements
                );
                if (commerceDataPlacement) {
                  setCommerceData({
                    OnLoadBeacon: carouselPlacements.OnLoadBeacon,
                    "page-uid": carouselPlacements.OnLoadBeacon,
                    placement: commerceDataPlacement
                  });
                }
                const butterflyKey = `viewHome_API_${isMobile ? "mobile" : "desktop"}-Butterfly1`;
                const butterflyDataPlacement = findCriteoPlacement(
                  butterflyKey,
                  carouselPlacements.placements
                );
                if (butterflyDataPlacement) {
                  setButterflyData({
                    OnLoadBeacon: carouselPlacements.OnLoadBeacon,
                    "page-uid": carouselPlacements.OnLoadBeacon,
                    placement: butterflyDataPlacement
                  });
                }
                const videoSpotlightKey = `viewHome_API_${isMobile ? "mobile" : "desktop"}-Video`;
                const videoSpotlightPlacement = findCriteoPlacement(
                  videoSpotlightKey,
                  carouselPlacements.placements
                );
                if (videoSpotlightPlacement) {
                  setVideoSpotlightData({
                    OnLoadBeacon: carouselPlacements.OnLoadBeacon,
                    "page-uid": carouselPlacements.OnLoadBeacon,
                    placement: videoSpotlightPlacement
                  });
                }
              } else if (carouselPlacementsError) {
                setSponsoredProductError(
                  sponsoredProductError
                );
                setCommerceError(
                  sponsoredProductError
                );
                setButterflyError(
                  sponsoredProductError
                );
                setVideoSpotlightError(
                  sponsoredProductError
                );
              }
              if (recommendedProductData2) {
                setRecommendedProductData(
                  recommendedProductData2
                );
              } else if (recommendedProductError2) {
                setRecommendedProductError(
                  recommendedProductError2
                );
              }
              if (commerceData) {
                setCommerceData(commerceData);
              } else if (commerceError) {
                setCommerceError(commerceError);
              }
              setIsLoadingCriteoData(false);
            };
            getCriteoHomePageResp();
          }
        } else {
          setIsLoadingCriteoData(true);
          const getCriteoSearchPageResp = async () => {
            setIsLoadingCriteoData(true);
            const sharedQueryConfigurationParams = {
              connection_timeout,
              endpoint,
              isMobile,
              sharedQueryParams,
              timeout
            };
            const pageIdBaseKey = isCategoryPage ? "viewCategory" : isBD() ? "viewSearchResult" : "viewSearchResults";
            const queryString = getQueryString({
              ...sharedQueryParams,
              category: categoryId,
              "event-type": isCategoryPage ? "viewCategory" : "viewSearchResult",
              filters: searchParams == null ? void 0 : searchParams.refine,
              item: searchResultDocs == null ? void 0 : searchResultDocs.reduce(
                (itemNumbers, {
                  group_id,
                  item_fsa_eligible,
                  item_chdi_eligible
                }) => {
                  if (group_id && !item_fsa_eligible && !item_chdi_eligible) {
                    if (itemNumbers) {
                      return `${itemNumbers}|${group_id}`;
                    } else {
                      return group_id;
                    }
                  }
                  return itemNumbers;
                },
                ""
              ),
              keywords: searchParams == null ? void 0 : searchParams.keyword,
              "list-size": listSize,
              "page-id": `${pageIdBaseKey}_API_${isMobile ? "mobile" : "desktop"}`,
              "page-number": searchParams == null ? void 0 : searchParams.currentPage
            });
            const {
              data: searchResponse,
              error: searchResponseError
            } = await queryCriteo({
              ...sharedQueryConfigurationParams,
              placementName: pageIdBaseKey,
              queryString,
              returnAllPlacements: true
            });
            if (searchResponse) {
              setSearchPageData(searchResponse);
            } else if (searchResponseError) {
              setSearchPageError(
                searchResponseError
              );
            }
            setIsLoadingCriteoData(false);
          };
          getCriteoSearchPageResp();
        }
      } else {
        if (isSearchPage && isSearchPageReady) {
          setIsLoadingCriteoData(false);
        }
      }
      ref.current = useEffectObjectString;
    }
  }, [
    categoryId,
    isCategoryPage,
    isSearchPage,
    isSearchPageReady,
    searchResultsItems,
    criteoUserCookie,
    isTargetingEnabled,
    searchParams == null ? void 0 : searchParams.keyword,
    searchParams == null ? void 0 : searchParams.refine,
    searchParams == null ? void 0 : searchParams.currentPage,
    isSearchResultsLoading,
    category,
    commerceData,
    commerceError,
    connection_timeout,
    endpoint,
    isCanada,
    isMobile,
    itemNumber,
    listSize,
    nolog,
    partnerId,
    price,
    regionId,
    searchParams,
    searchResultDocs,
    sponsoredProductError,
    timeout,
    pdpStatus
  ]);
  return /* @__PURE__ */ jsx(
    CriteoProviderContext.Provider,
    {
      value: {
        bdWHNumber,
        butterflyData,
        butterflyError,
        commerceData,
        commerceError,
        concurrency,
        isLoadingCriteoData,
        pdpStatus,
        isTargetingEnabled,
        productTileConfigData,
        recommendedProductData,
        recommendedProductError,
        searchPageData,
        searchPageError,
        sponsoredProductData,
        sponsoredProductError,
        videoSpotlightData,
        videoSpotlightError
      },
      children
    }
  );
};
const FakeCriteoProviderComponent = ({
  children
}) => {
  return /* @__PURE__ */ jsx(
    CriteoProviderContext.Provider,
    {
      value: {},
      children
    }
  );
};
const ProviderToUse = process.env.NEXT_PUBLIC_SITE === "CABD" ? FakeCriteoProviderComponent : CriteoProviderComponent;
const MemoizedCriteoProviderComponent = memo(ProviderToUse);
const CriteoProviderClientComponent = (props) => {
  const { bdWarehouseNumber, thirdPartyInfo, criteoUserCookie } = useBrowseContext();
  const { consentManagement } = thirdPartyInfo;
  const { isTargetingEnabled: isConsentManagementTargetingEnabled } = consentManagement;
  return /* @__PURE__ */ jsx(
    MemoizedCriteoProviderComponent,
    {
      isOneTrustTargetingEnabled: isConsentManagementTargetingEnabled,
      criteoUserCookie,
      bdWarehouseNumber: bdWarehouseNumber == null ? void 0 : bdWarehouseNumber.value,
      ...props
    }
  );
};
const useCriteo = () => {
  const context = useContext(CriteoProviderContext);
  if (context === void 0) {
    throw new Error("useCriteo must be used within a CriteoProvider");
  }
  return context;
};
CriteoProviderClientComponent.displayName = COMPONENT_NAME;
export {
  CriteoProviderClientComponent,
  useCriteo
};
